import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Grid,
    Divider,
    Chip,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Collapse,
    useTheme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DownloadIcon from '@mui/icons-material/Download';
import Link from '@mui/material/Link';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { orderService } from '../../../../api/services/orderService';
import { fileService } from '../../../../api/services/fileService';
import { getItemListCached } from '../../../../utils/cache/itemCache';
import { getDeliveryListCached } from '../../../../utils/cache/deliveryCache';
import { getLocationListCached } from '../../../../utils/cache/locationCache';
import { getUomListCached, fetchUomListCached } from '../../../../utils/cache/uomCache';

const SubOrderDetailDialog = ({ open, onClose, subOrderId, dyeingNoShort }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [subOrderData, setSubOrderData] = useState(null);
    const [dyeingEventExpanded, setDyeingEventExpanded] = useState(false);
    const [windingEventExpanded, setWindingEventExpanded] = useState(false);
    const [files, setFiles] = useState([]);
    const [filesLoading, setFilesLoading] = useState(false);
    const [uoms, setUoms] = useState([]);
    const loadingRef = useRef(false);
    const loadedSubOrderIdRef = useRef(null);
    const loadedDyeingNoShortRef = useRef(null);
    const uomsLoadedRef = useRef(false);

    // UOM 로드 함수 (한 번만 실행)
    const loadUoms = useCallback(async () => {
        if (uomsLoadedRef.current) {
            // 이미 로드된 경우 캐시된 데이터만 사용
            const cachedUoms = getUomListCached();
            if (cachedUoms && cachedUoms.length > 0) {
                setUoms(cachedUoms);
            }
            return;
        }

                try {
                    // 먼저 캐시된 데이터 확인
                    const cachedUoms = getUomListCached();
                    if (cachedUoms && cachedUoms.length > 0) {
                        setUoms(cachedUoms);
                uomsLoadedRef.current = true;
                    }
                    
                    // 최신 데이터 가져오기 (캐시가 없으면 API 호출)
                    const uomData = await fetchUomListCached(false);
                    if (uomData && uomData.length > 0) {
                        setUoms(uomData);
                uomsLoadedRef.current = true;
                    }
                } catch (err) {
                    console.error('Failed to load UOM list:', err);
                    // 에러가 발생해도 캐시된 데이터가 있으면 사용
                    const cachedUoms = getUomListCached();
                    if (cachedUoms && cachedUoms.length > 0) {
                        setUoms(cachedUoms);
                uomsLoadedRef.current = true;
            }
        }
    }, []);

    // 서브 오더 상세 정보 가져오기
    const fetchSubOrderDetail = useCallback(async () => {
        // dyeingNoShort가 있으면 그것을 사용, 없으면 subOrderId 사용
        const identifier = dyeingNoShort || subOrderId;
        if (!identifier || loadingRef.current) {
            return;
        }

        // 같은 identifier에 대해 이미 로드 중이거나 로드된 경우 스킵
        if (dyeingNoShort && loadedDyeingNoShortRef.current === dyeingNoShort && subOrderData) {
            return;
        }
        if (!dyeingNoShort && loadedSubOrderIdRef.current === subOrderId && subOrderData) {
            return;
        }

        loadingRef.current = true;
        setLoading(true);
        setError(null);
        
        try {
            let data;
            if (dyeingNoShort) {
                data = await orderService.getSubOrderDetailByDyeingNo(dyeingNoShort);
                loadedDyeingNoShortRef.current = dyeingNoShort;
            } else {
                data = await orderService.getSubOrderDetail(subOrderId);
                loadedSubOrderIdRef.current = subOrderId;
            }
            setSubOrderData(data);
        } catch (err) {
            setError(err.message || t('orders.subOrder.detail.fetchError'));
        } finally {
            setLoading(false);
            loadingRef.current = false;
        }
    }, [subOrderId, dyeingNoShort, subOrderData, t]);

    useEffect(() => {
        const identifier = dyeingNoShort || subOrderId;
        if (open && identifier) {
            // 팝업이 열릴 때 이전 데이터 초기화 (다른 identifier인 경우에만)
            if (dyeingNoShort && loadedDyeingNoShortRef.current !== dyeingNoShort) {
                setSubOrderData(null);
                setFiles([]);
                setFilesLoading(false);
                setError(null);
            } else if (!dyeingNoShort && loadedSubOrderIdRef.current !== subOrderId) {
                setSubOrderData(null);
                setFiles([]);
                setFilesLoading(false);
                setError(null);
            }
            
            // UOM 로드 (한 번만)
            loadUoms();
            
            // 서브 오더 상세 정보 가져오기
            fetchSubOrderDetail();
        } else if (!open) {
            // 팝업이 닫힐 때 데이터 초기화하지 않음 (재사용을 위해)
            // 단, 에러 상태만 초기화
            setError(null);
        }
    }, [open, subOrderId, dyeingNoShort, loadUoms, fetchSubOrderDetail]);

    useEffect(() => {
        if (subOrderData?.main_order_info?.order_files?.files) {
            fetchFilesDetails(subOrderData.main_order_info.order_files.files);
        } else {
            setFiles([]);
            setFilesLoading(false);
        }
    }, [subOrderData?.main_order_info?.order_files?.files]);

    const fetchFilesDetails = async (filesArray) => {
        if (!filesArray || filesArray.length === 0) {
            setFiles([]);
            return;
        }

        setFilesLoading(true);
        try {
            const filesWithDetails = await Promise.all(
                filesArray.map(async (file) => {
                    try {
                        const fileDetail = await fileService.getFile(file.file_id);
                        return {
                            file_id: file.file_id,
                            filename: file.file_name || fileDetail.filename,
                            download_url: fileDetail.download_url
                        };
                    } catch (err) {
                        console.error(`파일 상세 조회 실패 (${file.file_id}):`, err);
                        return {
                            file_id: file.file_id,
                            filename: file.file_name || '알 수 없는 파일',
                            download_url: null
                        };
                    }
                })
            );
            setFiles(filesWithDetails);
        } catch (err) {
            console.error('파일 목록 조회 실패:', err);
            setFiles([]);
        } finally {
            setFilesLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            // ISO 8601 형식의 날짜 문자열 처리
            // "+00:00" 형식도 지원하도록 정리
            let cleanedDate = dateString;
            
            // "+00:00" 형식을 "Z"로 변환 (UTC 표시)
            if (cleanedDate.endsWith('+00:00')) {
                cleanedDate = cleanedDate.replace('+00:00', 'Z');
            }
            // "+00:00Z" 같은 잘못된 형식도 정리
            cleanedDate = cleanedDate.replace(/\+00:00Z$/, 'Z');
            
            // dayjs로 파싱 (ISO 문자열은 자동으로 UTC로 인식됨)
            const parsed = dayjs(cleanedDate);
            if (!parsed.isValid()) {
                console.warn('Invalid date string:', dateString);
                return '-';
            }
            // 브라우저 로컬 시간으로 변환하여 표시
            return parsed.format('YYYY-MM-DD HH:mm:ss');
        } catch (error) {
            console.error('Error formatting date:', dateString, error);
            return '-';
        }
    };

    const formatDateOnly = (dateString) => {
        if (!dateString) return '-';
        // 날짜 문자열 정리 (끝의 중복 Z 제거 등)
        const cleanedDate = dateString.replace(/\+00:00Z$/, '+00:00').replace(/Z$/, '');
        const parsed = dayjs(cleanedDate);
        if (!parsed.isValid()) {
            console.warn('Invalid date string:', dateString);
            return '-';
        }
        // 브라우저 로컬 시간으로 변환하여 날짜만 표시
        return parsed.format('YYYY-MM-DD');
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'COMPLETED':
                return 'success';
            case 'IN_PROGRESS':
                return 'warning';
            case 'PENDING':
                return 'info';
            default:
                return 'default';
        }
    };

    const getCurrencySymbol = (currency) => {
        switch (currency) {
            case 'USD':
                return '$';
            case 'KRW':
                return '₩';
            case 'IDR':
                return 'Rp';
            default:
                return currency;
        }
    };

    if (loading) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
                <DialogContent>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Typography variant="h6" component="div">
                    {t('orders.subOrder.detail.title') || 'Sub-Order Detail Information'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('orders.subOrder.detail.subOrderId') || 'Sub-Order ID'}: {subOrderId}
                </Typography>
            </DialogTitle>
            
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {subOrderData && (
                    <Box>
                        {/* 기본 정보 */}
                        {subOrderData.main_order_info && (
                            <>
                                <Typography variant="h6" sx={{ mb: 2, mt: 1 }}>
                                    {t('orders.subOrder.detail.basicInfo')}
                                </Typography>
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.orderId')}</Typography>
                                        <Typography variant="body2">{subOrderData.main_order_info.order_id || '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.buyer')}</Typography>
                                        <Typography variant="body2">{subOrderData.main_order_info.buyer_name || '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.vendor')}</Typography>
                                        <Typography variant="body2">{subOrderData.main_order_info.vendor_name || '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.manager')}</Typography>
                                        <Typography variant="body2">{subOrderData.main_order_info.manager_name || '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.delivery')}</Typography>
                                        <Typography variant="body2">{subOrderData.main_order_info.delivery_name || '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.styleNo')}</Typography>
                                        <Typography variant="body2">{subOrderData.main_order_info.style_no || '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.fileNo')}</Typography>
                                        <Typography variant="body2">{subOrderData.main_order_info.file_no || '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.orderReceivedDate')}</Typography>
                                        <Typography variant="body2">
                                            {formatDateOnly(subOrderData.main_order_info.date_order_rcved)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.orderCreator')}</Typography>
                                        <Typography variant="body2">{subOrderData.main_order_info.created_by || '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.createdAt')}</Typography>
                                        <Typography variant="body2">
                                            {formatDate(subOrderData.main_order_info?.created_at || subOrderData.created_at)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.fields.fx_rate')}</Typography>
                                        <Typography variant="body2">
                                            {subOrderData.main_order_info.fx_base_currency && subOrderData.main_order_info.fx_quote_currency && subOrderData.main_order_info.fx_rate != null && subOrderData.main_order_info.fx_rate !== ''
                                                ? `${subOrderData.main_order_info.fx_base_currency} → ${subOrderData.main_order_info.fx_quote_currency}: ${Number(subOrderData.main_order_info.fx_rate).toLocaleString()}`
                                                : '-'}
                                        </Typography>
                                    </Grid>
                                    {files.length > 0 && (
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, fontWeight: 600 }}>
                                                {t('orders.subOrder.detail.attachedFiles', '첨부 파일')}
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                {files.map((file, index) => (
                                                    file.download_url ? (
                                                        <Link
                                                            key={file.file_id || index}
                                                            href={file.download_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 0.5,
                                                                textDecoration: 'none',
                                                                color: 'primary.main',
                                                                '&:hover': {
                                                                    textDecoration: 'underline'
                                                                }
                                                            }}
                                                        >
                                                            <DownloadIcon fontSize="small" />
                                                            <Typography variant="body2">
                                                                {file.filename || `파일 ${index + 1}`}
                                                            </Typography>
                                                        </Link>
                                                    ) : (
                                                        <Box key={file.file_id || index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <DownloadIcon fontSize="small" color="disabled" />
                                                            <Typography variant="body2" color="text.secondary">
                                                                {file.filename || `파일 ${index + 1}`} (다운로드 불가)
                                                            </Typography>
                                                        </Box>
                                                    )
                                                ))}
                                            </Box>
                                        </Grid>
                                    )}
                                    {filesLoading && (
                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CircularProgress size={16} />
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                    {t('orders.files.loading', '파일 목록 로딩 중...')}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    )}
                                </Grid>
                                <Divider sx={{ my: 2 }} />
                            </>
                        )}

                        {/* 상세 정보 */}
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            {t('orders.subOrder.detail.detailInfo')}
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.itemName') || '품명'}</Typography>
                                <Typography variant="body2">
                                    {(() => {
                                        if (!subOrderData.item_id) return '-';
                                        const items = getItemListCached() || [];
                                        const item = items.find(i => i.item_id === subOrderData.item_id);
                                        return item ? (item.name || item.item_name || '-') : (subOrderData.item_name || '-');
                                    })()}
                                </Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.color')}</Typography>
                                <Typography variant="body2">{subOrderData.color || '-'}</Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.quantity')}</Typography>
                                <Typography variant="body2">{subOrderData.qty || '-'}</Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.unitPrice')}</Typography>
                                <Typography variant="body2">
                                    {subOrderData.price ? `${getCurrencySymbol(subOrderData.price_currency)}${subOrderData.price.toLocaleString()}` : '-'}
                                </Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.totalAmount')}</Typography>
                                <Typography variant="body2">
                                    {subOrderData.price && subOrderData.qty ? 
                                        `${getCurrencySymbol(subOrderData.price_currency)}${(subOrderData.price * subOrderData.qty).toLocaleString()}` : '-'}
                                </Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.currency')}</Typography>
                                <Typography variant="body2">{subOrderData.price_currency || '-'}</Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.createdAt')}</Typography>
                                <Typography variant="body2">{formatDate(subOrderData.created_at)}</Typography>
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        {/* 염색 요청 */}
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            {t('orders.subOrder.detail.dyeingRequest')}
                        </Typography>
                        {subOrderData.dyeing_request_info ? (
                            <>
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.dyeingNo')}</Typography>
                                        <Typography variant="body2">{subOrderData.dyeing_request_info.dyeing_no_short || '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.dyeingRequestType')}</Typography>
                                        <Typography variant="body2">{subOrderData.dyeing_request_info.dyeing_request_type || '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.rawYarn')}</Typography>
                                        <Typography variant="body2">
                                            {(() => {
                                                const rawYarnId = subOrderData.dyeing_request_info?.raw_yarn_id || subOrderData.raw_yarn_id;
                                                if (!rawYarnId) return '-';
                                                const items = getItemListCached() || [];
                                                const rawYarnItem = items.find(i => i.item_id === rawYarnId);
                                                return rawYarnItem ? (rawYarnItem.name || rawYarnItem.item_name || '-') : '-';
                                            })()}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.requestCheeseNum')}</Typography>
                                        <Typography variant="body2">{subOrderData.dyeing_request_info.request_cheese_num || '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.repeat')}</Typography>
                                        <Typography variant="body2">{subOrderData.dyeing_request_info.repeat_dyeing_no_short || '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.confirm')}</Typography>
                                        <Typography variant="body2">
                                            {subOrderData.dyeing_request_info.swatch_approval_required ? 'VENDOR' : 'SELF'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.urgent')}</Typography>
                                        <Typography variant="body2">
                                            {subOrderData.dyeing_request_info.urgent_flag ? t('common.yes') : t('common.no')}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.requester')}</Typography>
                                        <Typography variant="body2">{subOrderData.dyeing_request_info.created_by || '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.requestDate')}</Typography>
                                        <Typography variant="body2">
                                            {formatDateOnly(subOrderData.dyeing_request_info.created_at)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.remark')}</Typography>
                                        <Typography variant="body2">{subOrderData.dyeing_request_info.remark || '-'}</Typography>
                                    </Grid>
                                </Grid>

                                {/* 스와치 정보 테이블 */}
                                {subOrderData.dyeing_request_info.swatch_info && subOrderData.dyeing_request_info.swatch_info.info && subOrderData.dyeing_request_info.swatch_info.info.length > 0 && (
                                    <>
                                                                    <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
                                {t('orders.subOrder.detail.swatchInfo')}
                            </Typography>
                                        <TableContainer component={Paper} sx={{ mb: 2 }}>
                                            <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.75rem' } }}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>{t('orders.subOrder.detail.swatchQty')}</TableCell>
                                                        <TableCell>{t('orders.subOrder.detail.swatchSize')}</TableCell>
                                                        <TableCell>{t('orders.subOrder.detail.swatchRemark')}</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {subOrderData.dyeing_request_info.swatch_info.info.map((swatch, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>{swatch.swatch_qty || '-'}</TableCell>
                                                            <TableCell>{swatch.swatch_size || '-'}</TableCell>
                                                            <TableCell>{swatch.swatch_remark || '-'}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </>
                                )}
                            </>
                        ) : (
                            <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {t('orders.subOrder.detail.dyeingRequestBefore')}
                                </Typography>
                            </Box>
                        )}
                        <Divider sx={{ my: 2 }} />

                        {/* 염색 공정 정보 */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">
                                {t('orders.subOrder.detail.dyeingProcess')}
                            </Typography>
                            {subOrderData.dyeing_process_event && subOrderData.dyeing_process_event.length > 0 && (
                                <IconButton
                                    onClick={() => setDyeingEventExpanded(!dyeingEventExpanded)}
                                    sx={{ ml: 1 }}
                                >
                                    {dyeingEventExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                            )}
                        </Box>
                        {subOrderData.dyeing_process_info && subOrderData.dyeing_process_info.length > 0 ? (
                            <TableContainer component={Paper} sx={{ mb: 2 }}>
                                <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.75rem' } }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('orders.subOrder.detail.productionId')}</TableCell>
                                            <TableCell>{t('orders.subOrder.detail.recipeId')}</TableCell>
                                            <TableCell>{t('orders.subOrder.detail.cheeseNum')}</TableCell>
                                            <TableCell>{t('orders.subOrder.detail.confirm')}</TableCell>
                                            <TableCell>{t('orders.subOrder.detail.urgent')}</TableCell>
                                            <TableCell>{t('orders.subOrder.detail.machineNo')}</TableCell>
                                            <TableCell>{t('orders.subOrder.detail.status')}</TableCell>
                                            <TableCell>{t('orders.subOrder.detail.dyeingStart')}</TableCell>
                                            <TableCell>{t('orders.subOrder.detail.dyeingEnd')}</TableCell>
                                            <TableCell>{t('orders.subOrder.detail.storage')}</TableCell>
                                            <TableCell>{t('orders.subOrder.detail.lastUpdate')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(() => {
                                            // 중복 제거: production_id_short와 recipe_id_short의 조합으로 고유한 항목만 유지
                                            const uniqueDyeingProcess = [];
                                            const seenKeys = new Set();
                                            
                                            subOrderData.dyeing_process_info.forEach(dyeing => {
                                                const uniqueKey = `${dyeing.production_id_short}-${dyeing.recipe_id_short}`;
                                                if (!seenKeys.has(uniqueKey)) {
                                                    seenKeys.add(uniqueKey);
                                                    uniqueDyeingProcess.push(dyeing);
                                                }
                                            });
                                            
                                            return uniqueDyeingProcess.map((dyeing, index) => {
                                                const isDeleted = dyeing.is_deleted === true;
                                                return (
                                                    <TableRow 
                                                        key={index}
                                                        sx={{
                                                            ...(isDeleted && {
                                                                '& .MuiTableCell-root': {
                                                                    textDecoration: 'line-through',
                                                                    opacity: 0.6,
                                                                    color: 'text.secondary'
                                                                }
                                                            })
                                                        }}
                                                    >
                                                        <TableCell>{dyeing.production_id_short || '-'}</TableCell>
                                                        <TableCell>{dyeing.recipe_id_short || '-'}</TableCell>
                                                        <TableCell>
                                                            {dyeing.work_cheese_num_float != null 
                                                                ? Number(dyeing.work_cheese_num_float).toFixed(2) 
                                                                : (dyeing.work_cheese_num != null ? dyeing.work_cheese_num : '-')}
                                                        </TableCell>
                                                        <TableCell>{dyeing.swatch_approval_required ? 'VENDOR' : 'SELF'}</TableCell>
                                                        <TableCell>{dyeing.priority === 'urgent' ? t('common.yes') : t('common.no')}</TableCell>
                                                        <TableCell>{dyeing.machine_no || '-'}</TableCell>
                                                        <TableCell>
                                                            <Chip 
                                                                label={dyeing.dyeing_status_string || '-'} 
                                                                size="small" 
                                                                sx={{ fontSize: '0.7rem' }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>{formatDate(dyeing.boiling_start)}</TableCell>
                                                        <TableCell>{formatDate(dyeing.boiling_end)}</TableCell>
                                                        <TableCell>{dyeing.cheese_rack_code || '-'}</TableCell>
                                                        <TableCell>{formatDate(dyeing.updated_at || dyeing.created_at)}</TableCell>
                                                    </TableRow>
                                                );
                                            });
                                        })()}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    공정 없음
                                </Typography>
                            </Box>
                        )}

                        {/* 염색 공정 이벤트 (토글) */}
                        <Collapse in={dyeingEventExpanded}>
                            <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
                                {t('orders.subOrder.detail.dyeingProcessEvent')}
                            </Typography>
                            {subOrderData.dyeing_process_event && subOrderData.dyeing_process_event.length > 0 ? (
                                <TableContainer component={Paper} sx={{ mb: 2 }}>
                                    <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.75rem' } }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>{t('orders.subOrder.detail.productionId')}</TableCell>
                                                <TableCell>{t('orders.subOrder.detail.eventName')}</TableCell>
                                                <TableCell>{t('orders.subOrder.detail.author')}</TableCell>
                                                <TableCell>{t('orders.subOrder.detail.value1')}</TableCell>
                                                <TableCell>{t('orders.subOrder.detail.value2')}</TableCell>
                                                <TableCell>{t('orders.subOrder.detail.remark')}</TableCell>
                                                <TableCell>{t('orders.subOrder.detail.createdAt')}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {subOrderData.dyeing_process_event.map((event, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{event.production_id_short || '-'}</TableCell>
                                                    <TableCell>{event.event_name || '-'}</TableCell>
                                                    <TableCell>{event.created_by || '-'}</TableCell>
                                                    <TableCell>{event.value_1_str || '-'}</TableCell>
                                                    <TableCell>{event.value_2_str || '-'}</TableCell>
                                                    <TableCell>{event.note || '-'}</TableCell>
                                                    <TableCell>{formatDate(event.created_at)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                                            <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {t('orders.subOrder.detail.noEvent')}
                                </Typography>
                            </Box>
                            )}
                        </Collapse>

                        <Divider sx={{ my: 2 }} />

                        {/* 와인딩 공정 */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">
                                {t('orders.subOrder.detail.windingProcess')}
                            </Typography>
                            {subOrderData.winding_process_event && subOrderData.winding_process_event.length > 0 && (
                                <IconButton
                                    onClick={() => setWindingEventExpanded(!windingEventExpanded)}
                                    sx={{ ml: 1 }}
                                >
                                    {windingEventExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                            )}
                        </Box>
                        {subOrderData.winding_process_info && subOrderData.winding_process_info.length > 0 ? (
                            <TableContainer component={Paper} sx={{ mb: 2 }}>
                                <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.75rem' } }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('orders.subOrder.detail.windingId')}</TableCell>
                                            <TableCell>{t('orders.subOrder.detail.requestQty')}</TableCell>
                                            <TableCell>{t('orders.subOrder.detail.productionQty')}</TableCell>
                                            <TableCell>{t('orders.subOrder.detail.targetDate')}</TableCell>
                                            <TableCell>{t('orders.subOrder.detail.plannedDate')}</TableCell>
                                            <TableCell>{t('orders.subOrder.detail.status')}</TableCell>
                                            <TableCell>{t('orders.subOrder.detail.dryingRequired')}</TableCell>
                                            <TableCell>{t('orders.subOrder.detail.lastUpdate')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {subOrderData.winding_process_info.map((winding, index) => (
                                            <TableRow key={winding.winding_id || index}>
                                                <TableCell>{winding.winding_id_short || '-'}</TableCell>
                                                <TableCell>{winding.qty_requested || '-'}</TableCell>
                                                <TableCell>{winding.qty_produced || '-'}</TableCell>
                                                <TableCell>{formatDateOnly(winding.target_date)}</TableCell>
                                                <TableCell>{formatDate(winding.date_planned)}</TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={winding.winding_status_name || '-'} 
                                                        color={getStatusColor(winding.winding_status_name)}
                                                        size="small"
                                                        sx={{ fontSize: '0.7rem' }}
                                                    />
                                                </TableCell>
                                                <TableCell>{winding.drying_required ? t('common.yes') : t('common.no')}</TableCell>
                                                <TableCell>{formatDate(winding.updated_at || winding.created_at)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {t('orders.subOrder.detail.noProcess')}
                                </Typography>
                            </Box>
                        )}

                        {/* 와인딩 공정 이벤트 (토글) */}
                        <Collapse in={windingEventExpanded}>
                            <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
                                {t('orders.subOrder.detail.windingProcessEvent')}
                            </Typography>
                            {subOrderData.winding_process_event && subOrderData.winding_process_event.length > 0 ? (
                                <TableContainer component={Paper} sx={{ mb: 2 }}>
                                    <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.75rem' } }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>{t('orders.subOrder.detail.windingId')}</TableCell>
                                                <TableCell>{t('orders.subOrder.detail.eventName')}</TableCell>
                                                <TableCell>{t('orders.subOrder.detail.author')}</TableCell>
                                                <TableCell>{t('orders.subOrder.detail.remark')}</TableCell>
                                                <TableCell>{t('orders.subOrder.detail.createdAt')}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {subOrderData.winding_process_event.map((event, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{event.winding_id_short || '-'}</TableCell>
                                                    <TableCell>{event.event_name || '-'}</TableCell>
                                                    <TableCell>{event.created_by || '-'}</TableCell>
                                                    <TableCell>{event.note || '-'}</TableCell>
                                                    <TableCell>{formatDate(event.created_at)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                                            <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {t('orders.subOrder.detail.noEvent')}
                                </Typography>
                            </Box>
                            )}
                        </Collapse>

                        <Divider sx={{ my: 2 }} />

                        {/* 출고 요청 */}
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            {t('orders.subOrder.detail.giRequest')}
                        </Typography>
                        {subOrderData.gi_request_info && subOrderData.gi_request_info.length > 0 ? (
                            <>
                                {subOrderData.gi_request_info.map((giRequest, requestIndex) => {
                                    const isRequestDeleted = giRequest.is_deleted === true;
                                    return (
                                    <Box key={giRequest.gi_request_id || requestIndex} sx={{ mb: 3, ...(isRequestDeleted && { opacity: 0.6, color: 'text.secondary' }) }}>
                                        <Grid container spacing={2} sx={{ mb: 3 }}>
                                            <Grid item xs={6} md={3}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.giRequestId')}</Typography>
                                                <Typography variant="body2" sx={isRequestDeleted ? { textDecoration: 'line-through' } : {}}>{giRequest.gi_request_id_short || giRequest.gi_request_id || '-'}</Typography>
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.delivery')}</Typography>
                                                <Typography variant="body2" sx={isRequestDeleted ? { textDecoration: 'line-through' } : {}}>
                                                    {(() => {
                                                        const deliveryId = giRequest?.delivery_id;
                                                        if (!deliveryId) return '-';
                                                        const deliveries = getDeliveryListCached() || [];
                                                        const delivery = deliveries.find(d => d.delivery_id === deliveryId);
                                                        return delivery ? (delivery.delivery_name || delivery.name || '-') : '-';
                                                    })()}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.requestDate')}</Typography>
                                                <Typography variant="body2" sx={isRequestDeleted ? { textDecoration: 'line-through' } : {}}>
                                                    {formatDateOnly(giRequest.created_at)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.plannedDispatchAt')}</Typography>
                                                <Typography variant="body2" sx={isRequestDeleted ? { textDecoration: 'line-through' } : {}}>
                                                    {formatDateOnly(giRequest.planned_dispatch_at)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.requester')}</Typography>
                                                <Typography variant="body2" sx={isRequestDeleted ? { textDecoration: 'line-through' } : {}}>{giRequest.created_by || '-'}</Typography>
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.lastUpdate')}</Typography>
                                                <Typography variant="body2" sx={isRequestDeleted ? { textDecoration: 'line-through' } : {}}>
                                                    {formatDate(giRequest.updated_at || giRequest.created_at)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.remark')}</Typography>
                                                <Typography variant="body2" sx={isRequestDeleted ? { textDecoration: 'line-through' } : {}}>{giRequest.remarks || giRequest.remark || '-'}</Typography>
                                            </Grid>
                                        </Grid>

                                        {/* 출고 요청 라인 정보 테이블 */}
                                        {giRequest.lines && giRequest.lines.length > 0 && (
                                            <TableContainer component={Paper} sx={{ mb: 2 }}>
                                                <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.75rem' } }}>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>NO</TableCell>
                                                            <TableCell>DOC_TYPE</TableCell>
                                                            <TableCell>DOC_ID</TableCell>
                                                            <TableCell>ITEM</TableCell>
                                                            <TableCell>QTY</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {giRequest.lines.map((line, index) => {
                                                            const isDeleted = line.is_deleted === true;
                                                            const items = getItemListCached() || [];
                                                            const item = items.find(i => i.item_id === line.item_id);
                                                            const itemName = item ? (item.name || item.item_name || '-') : '-';
                                                            
                                                            // 수량 포맷팅: 소수점 이하 의미없는 0 제거 및 천 단위 콤마
                                                            const formatQty = (qty) => {
                                                                if (qty == null || qty === '') return '-';
                                                                const num = Number(qty);
                                                                if (isNaN(num)) return qty;
                                                                // 정수면 정수로, 소수면 의미있는 소수점만 표시
                                                                const formatted = num % 1 === 0 ? num.toString() : num.toString().replace(/\.?0+$/, '');
                                                                // 천 단위 콤마 추가
                                                                return formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                                                            };
                                                            
                                                            return (
                                                                <TableRow 
                                                                    key={line.line_no || index}
                                                                    sx={{
                                                                        ...(isDeleted && {
                                                                            '& .MuiTableCell-root': {
                                                                                textDecoration: 'line-through',
                                                                                opacity: 0.6,
                                                                                color: 'text.secondary'
                                                                            }
                                                                        })
                                                                    }}
                                                                >
                                                                    <TableCell>{line.line_no || index + 1}</TableCell>
                                                                    <TableCell>{line.doc_type || '-'}</TableCell>
                                                                    <TableCell>{line.doc_ref_no || '-'}</TableCell>
                                                                    <TableCell>{itemName}</TableCell>
                                                                    <TableCell>{formatQty(line.qty)}</TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                        {requestIndex < subOrderData.gi_request_info.length - 1 && <Divider sx={{ my: 2 }} />}
                                    </Box>
                                    );
                                })}
                            </>
                        ) : (
                            <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {t('orders.subOrder.detail.giRequestBefore')}
                                </Typography>
                            </Box>
                        )}

                        <Divider sx={{ my: 2 }} />

                        {/* 출고 LOT */}
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            {t('orders.subOrder.detail.giLot')}
                        </Typography>
                        {subOrderData.gi_lot_info && subOrderData.gi_lot_info.length > 0 ? (
                            <>
                                {subOrderData.gi_lot_info.map((lot, lotIndex) => {
                                    const isLotDeleted = lot.is_deleted === true;
                                    return (
                                    <Box key={lot.lot_id || lotIndex} sx={{ mb: 3 }}>
                                        {/* LOT 기본 정보 */}
                                        <Box sx={{ mb: 2, ...(isLotDeleted && { opacity: 0.6, color: 'text.secondary' }) }}>
                                            {/* 첫 번째 행: LOT ID, LOT 번호, 예정 출고일, 작업 교대 */}
                                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                                <Grid item xs={6} md={3}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>LOT ID</Typography>
                                                    <Typography variant="body2" sx={isLotDeleted ? { textDecoration: 'line-through' } : {}}>{lot.lot_id || '-'}</Typography>
                                                </Grid>
                                                <Grid item xs={6} md={3}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.lotNo')}</Typography>
                                                    <Typography variant="body2" sx={isLotDeleted ? { textDecoration: 'line-through' } : {}}>{lot.lot_no || '-'}</Typography>
                                                </Grid>
                                                <Grid item xs={6} md={3}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.expectedGiAt')}</Typography>
                                                    <Typography variant="body2" sx={isLotDeleted ? { textDecoration: 'line-through' } : {}}>
                                                        {formatDate(lot.expected_gi_at)}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6} md={3}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>WORK SLOT</Typography>
                                                    <Typography variant="body2" sx={isLotDeleted ? { textDecoration: 'line-through' } : {}}>{lot.work_shift || '-'}</Typography>
                                                </Grid>
                                            </Grid>

                                            {/* 두 번째 행: 문서 유형, 문서 ID */}
                                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                                <Grid item xs={6} md={3}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>DOC TYPE</Typography>
                                                    <Typography variant="body2" sx={isLotDeleted ? { textDecoration: 'line-through' } : {}}>{lot.doc_type || '-'}</Typography>
                                                </Grid>
                                                <Grid item xs={6} md={3}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>DOC ID</Typography>
                                                    <Typography variant="body2" sx={isLotDeleted ? { textDecoration: 'line-through' } : {}}>{lot.doc_id || '-'}</Typography>
                                                </Grid>
                                            </Grid>

                                            {/* 세 번째 행: 배송지, 주소 */}
                                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                                <Grid item xs={6} md={3}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.delivery')}</Typography>
                                                    <Typography variant="body2" sx={isLotDeleted ? { textDecoration: 'line-through' } : {}}>
                                                        {(() => {
                                                            const deliveryId = lot.delivery_id;
                                                            if (!deliveryId) return '-';
                                                            const deliveries = getDeliveryListCached() || [];
                                                            const delivery = deliveries.find(d => d.delivery_id === deliveryId);
                                                            return delivery ? (delivery.delivery_name || delivery.name || '-') : '-';
                                                        })()}
                                                    </Typography>
                                                </Grid>
                                                {(lot.address1 || lot.city || lot.state || lot.postcode || lot.country) && (
                                                    <Grid item xs={6} md={9}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.address')}</Typography>
                                                        <Typography variant="body2" sx={isLotDeleted ? { textDecoration: 'line-through' } : {}}>
                                                            {[
                                                                lot.address1,
                                                                lot.address2,
                                                                lot.city,
                                                                lot.state,
                                                                lot.postcode,
                                                                lot.country
                                                            ].filter(Boolean).join(', ') || '-'}
                                                        </Typography>
                                                    </Grid>
                                                )}
                                            </Grid>

                                            {/* 네 번째 행: 속성 (각 속성을 개별 필드로 표시) */}
                                            {lot.attributes && Object.keys(lot.attributes).length > 0 && (
                                                <Grid container spacing={2}>
                                                    {Object.entries(lot.attributes).map(([key, value]) => {
                                                        // 속성 키 변환: customs_bc25 -> BC25, tax_npwp -> NPWP
                                                        const formatAttributeKey = (attrKey) => {
                                                            // 언더스코어로 분리하고 마지막 부분을 대문자로 변환
                                                            const parts = attrKey.split('_');
                                                            const lastPart = parts[parts.length - 1];
                                                            return lastPart.toUpperCase();
                                                        };
                                                        
                                                        return (
                                                            <Grid item xs={6} md={3} key={key}>
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{formatAttributeKey(key)}</Typography>
                                                                <Typography variant="body2" sx={isLotDeleted ? { textDecoration: 'line-through' } : {}}>{value || '-'}</Typography>
                                                            </Grid>
                                                        );
                                                    })}
                                                </Grid>
                                            )}
                                        </Box>

                                        {/* LOT 라인 정보 테이블 */}
                                        {lot.lines && lot.lines.length > 0 && (
                                            <TableContainer component={Paper} sx={{ mb: 2 }}>
                                                <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.75rem' } }}>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>NO</TableCell>
                                                            <TableCell>ITEM</TableCell>
                                                            <TableCell>QTY</TableCell>
                                                            <TableCell>{t('orders.subOrder.detail.qtyBox')}</TableCell>
                                                            <TableCell>{t('orders.subOrder.detail.qcStatus')}</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {lot.lines.map((line, lineIndex) => {
                                                            const isLineDeleted = line.is_deleted === true;
                                                            const items = getItemListCached() || [];
                                                            const item = items.find(i => i.item_id === line.item_id);
                                                            const itemName = item ? (item.name || item.item_name || '-') : '-';
                                                            
                                                            // 수량 포맷팅: 소수점 이하 의미없는 0 제거 및 천 단위 콤마
                                                            const formatQty = (qty) => {
                                                                if (qty == null || qty === '') return '-';
                                                                const num = Number(qty);
                                                                if (isNaN(num)) return qty;
                                                                const formatted = num % 1 === 0 ? num.toString() : num.toString().replace(/\.?0+$/, '');
                                                                return formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                                                            };
                                                            
                                                            return (
                                                                <TableRow 
                                                                    key={line.lot_line_id || lineIndex}
                                                                    sx={{
                                                                        ...(isLineDeleted && {
                                                                            '& .MuiTableCell-root': {
                                                                                textDecoration: 'line-through',
                                                                                opacity: 0.6,
                                                                                color: 'text.secondary'
                                                                            }
                                                                        })
                                                                    }}
                                                                >
                                                                    <TableCell>{lineIndex + 1}</TableCell>
                                                                    <TableCell>{itemName}</TableCell>
                                                                    <TableCell>{formatQty(line.qty)}</TableCell>
                                                                    <TableCell>{line.qty_box || '-'}</TableCell>
                                                                    <TableCell>{line.qc_status || '-'}</TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                    </Box>
                                    );
                                })}
                            </>
                        ) : (
                            <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {t('orders.subOrder.detail.giLotBefore')}
                                </Typography>
                            </Box>
                        )}

                        <Divider sx={{ my: 2 }} />

                        {/* 출고 등록 */}
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            {t('orders.subOrder.detail.giPost')}
                        </Typography>
                        {subOrderData.gi_post_info && subOrderData.gi_post_info.length > 0 ? (
                            <>
                                {subOrderData.gi_post_info.map((giPost, postIndex) => {
                                    const isPostDeleted = giPost.is_deleted === true;
                                    return (
                                    <Box key={giPost.gi_post_id || postIndex} sx={{ mb: 3, ...(isPostDeleted && { opacity: 0.6, color: 'text.secondary' }) }}>
                                        <Grid container spacing={2} sx={{ mb: 3 }}>
                                            <Grid item xs={6} md={3}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.giPostId')}</Typography>
                                                <Typography variant="body2" sx={isPostDeleted ? { textDecoration: 'line-through' } : {}}>{giPost.gi_post_id || '-'}</Typography>
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.giPostNo')}</Typography>
                                                <Typography variant="body2" sx={isPostDeleted ? { textDecoration: 'line-through' } : {}}>{giPost.gi_post_no || '-'}</Typography>
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.outboundLotNo')}</Typography>
                                                <Typography variant="body2" sx={isPostDeleted ? { textDecoration: 'line-through' } : {}}>{giPost.outbound_lot_no || '-'}</Typography>
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.giTime')}</Typography>
                                                <Typography variant="body2" sx={isPostDeleted ? { textDecoration: 'line-through' } : {}}>
                                                    {formatDate(giPost.gi_time)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.delivery')}</Typography>
                                                <Typography variant="body2" sx={isPostDeleted ? { textDecoration: 'line-through' } : {}}>
                                                    {(() => {
                                                        const deliveryId = giPost?.to_delivery_id;
                                                        if (!deliveryId) return '-';
                                                        const deliveries = getDeliveryListCached() || [];
                                                        const delivery = deliveries.find(d => d.delivery_id === deliveryId);
                                                        return delivery ? (delivery.delivery_name || delivery.name || '-') : '-';
                                                    })()}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.customsDocNo')}</Typography>
                                                <Typography variant="body2" sx={isPostDeleted ? { textDecoration: 'line-through' } : {}}>{giPost.customs_doc_no || '-'}</Typography>
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.createdBy')}</Typography>
                                                <Typography variant="body2" sx={isPostDeleted ? { textDecoration: 'line-through' } : {}}>{giPost.created_by || '-'}</Typography>
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.createdAt')}</Typography>
                                                <Typography variant="body2" sx={isPostDeleted ? { textDecoration: 'line-through' } : {}}>
                                                    {formatDate(giPost.created_at)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{t('orders.subOrder.detail.remark')}</Typography>
                                                <Typography variant="body2" sx={isPostDeleted ? { textDecoration: 'line-through' } : {}}>{giPost.remarks || '-'}</Typography>
                                            </Grid>
                                        </Grid>

                                        {/* 출고 등록 라인 정보 테이블 */}
                                        {giPost.lines && giPost.lines.length > 0 && (
                                            <TableContainer component={Paper} sx={{ mb: 2 }}>
                                                <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.75rem' } }}>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>NO</TableCell>
                                                            <TableCell>ITEM</TableCell>
                                                            <TableCell>QTY</TableCell>
                                                            <TableCell>UOM</TableCell>
                                                            <TableCell>FROM LOCATION</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {giPost.lines.map((line, index) => {
                                                            const items = getItemListCached() || [];
                                                            const item = items.find(i => i.item_id === line.item_id);
                                                            const itemName = item ? (item.name || item.item_name || '-') : '-';
                                                            
                                                            // UOM은 state에서 가져오거나, 없으면 캐시에서 가져오기
                                                            const uomList = uoms.length > 0 ? uoms : (getUomListCached() || []);
                                                            const uom = uomList.find(u => u.uom_id === line.uom_id);
                                                            const uomName = uom ? (uom.code || uom.name || '-') : '-';
                                                            
                                                            const locations = getLocationListCached() || [];
                                                            const location = locations.find(l => l.location_id === line.from_location_id);
                                                            const locationName = location ? `${location.warehouse || ''}${location.warehouse && location.bin_code ? ' - ' : ''}${location.bin_code || ''}`.trim() || String(location.location_id) : (line.from_location_id || '-');
                                                            
                                                            // 수량 포맷팅: 소수점 이하 의미없는 0 제거 및 천 단위 콤마
                                                            const formatQtyValue = (qty) => {
                                                                if (qty == null || qty === '') return '-';
                                                                const num = Number(qty);
                                                                if (isNaN(num)) return qty;
                                                                // 정수면 정수로, 소수면 의미있는 소수점만 표시
                                                                const formatted = num % 1 === 0 
                                                                    ? num.toLocaleString('en-US')
                                                                    : parseFloat(num.toFixed(10)).toString().replace(/\.0+$/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                                                                return formatted;
                                                            };
                                                            
                                                            return (
                                                                <TableRow 
                                                                    key={line.line_no || index}
                                                                    sx={{
                                                                        ...(isPostDeleted && {
                                                                            '& .MuiTableCell-root': {
                                                                                textDecoration: 'line-through',
                                                                                opacity: 0.6,
                                                                                color: 'text.secondary'
                                                                            }
                                                                        })
                                                                    }}
                                                                >
                                                                    <TableCell>{line.line_no || index + 1}</TableCell>
                                                                    <TableCell>{itemName}</TableCell>
                                                                    <TableCell>{formatQtyValue(line.qty)}</TableCell>
                                                                    <TableCell>{uomName}</TableCell>
                                                                    <TableCell>{locationName}</TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                        {postIndex < subOrderData.gi_post_info.length - 1 && <Divider sx={{ my: 2 }} />}
                                    </Box>
                                    );
                                })}
                            </>
                        ) : (
                            <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {t('orders.subOrder.detail.giPostBefore')}
                                </Typography>
                            </Box>
                        )}

                    </Box>
                )}
            </DialogContent>
            
            <DialogActions>
                <Button 
                    onClick={onClose} 
                    color="primary"
                    sx={{
                        color: theme.palette.mode === 'dark' ? '#ffffff !important' : undefined,
                        '& .MuiButton-label': {
                            color: theme.palette.mode === 'dark' ? '#ffffff !important' : undefined,
                        }
                    }}
                >
                    {t('common.close') || 'Close'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SubOrderDetailDialog;
